import {
  getAppliesToRule,
  safeParseJson,
  normalizeToUTCMidnight,
} from "../../constants/utils";
import { DERIVED_FROM_TYPE, DOW_DISCOUNT_TYPE } from "./constants";

// Mirrors the legacy PromotionSummary filters (day-of-week zero-discount
// exclusion + stay-date coverage) so the stacked view's rows and count badge
// share one source of truth. Returns [{ promo, rule }].
export const filterVisiblePromos = (promos, date, dayOfWeek) =>
  (promos || []).reduce((visible, promo) => {
    const rule = safeParseJson(promo.ruleJson);
    const discountVO = rule?.ruleVO?.discountVO;

    if (
      discountVO?.discountTypeDescription === DOW_DISCOUNT_TYPE &&
      discountVO?.percentDiscountMap?.[dayOfWeek] === 0
    ) {
      return visible;
    }

    const { stayStartDate, stayEndDate } = getAppliesToRule(rule);
    if (
      (date &&
        stayStartDate &&
        date.isBefore(normalizeToUTCMidnight(stayStartDate))) ||
      (date && stayEndDate && date.isAfter(normalizeToUTCMidnight(stayEndDate)))
    ) {
      return visible;
    }

    visible.push({ promo, rule });
    return visible;
  }, []);

// A derived rate's priceConfig also carries entries inherited from its
// ancestors (marked with derivedFromId); its own adjustments have none.
export const getOwnPriceConfig = (dr) =>
  (dr?.priceConfig || []).filter((pc) => !pc.derivedFromId);

// Groups pre-filtered derived rates into [{ parent, descendants }] for the
// stacked view. A rate is top-level when it is not derived from another
// derived rate, or when its parent was filtered out by the caller's
// roomtype/date applicability checks (orphans are promoted to top level).
// Descendants are collected depth-first with a cycle guard. Each descendant
// entry carries the hierarchy metadata the row needs to draw its indentation
// and tree connectors (mirrors the property-config rate-plan tree):
//   { rate, depth, isLastSibling, ancestorRailDepths }
// - depth: 1 for direct children, 2 for their children, and so on
// - isLastSibling: closes its sibling group, so its rail ends at the elbow
// - ancestorRailDepths: rail columns (0-based) that continue through this
//   row because an ancestor still has siblings rendered after this subtree
// Rates nested deeper than `maxDepth` are omitted entirely (not rendered and
// not counted), mirroring the property-config rate-plan tree's display cap.
export const buildStackedPromotionGroups = (
  derivedRates,
  maxDepth = Infinity
) => {
  const rates = derivedRates || [];
  const byId = new Map(rates.map((dr) => [dr.id, dr]));

  const isChild = (dr) =>
    dr.derivedFromType === DERIVED_FROM_TYPE.DERIVED_RATE &&
    byId.has(dr.derivedFrom);

  const childrenByParent = new Map();
  rates.filter(isChild).forEach((dr) => {
    const siblings = childrenByParent.get(dr.derivedFrom) || [];
    siblings.push(dr);
    childrenByParent.set(dr.derivedFrom, siblings);
  });

  const collectDescendants = ({
    parentId,
    depth,
    ancestorsContinuing,
    visited,
    out,
  }) => {
    if (depth > maxDepth) return;
    const children = (childrenByParent.get(parentId) || [])
      .slice()
      .sort((a, b) => a.id - b.id);
    children.forEach((child, index) => {
      if (visited.has(child.id)) return;
      visited.add(child.id);
      const isLastSibling = index === children.length - 1;
      const ancestorRailDepths = [];
      for (let d = 0; d < depth - 1; d += 1) {
        if (ancestorsContinuing[d]) ancestorRailDepths.push(d);
      }
      out.push({ rate: child, depth, isLastSibling, ancestorRailDepths });
      const childAncestors = ancestorsContinuing.slice();
      childAncestors[depth - 1] = !isLastSibling;
      collectDescendants({
        parentId: child.id,
        depth: depth + 1,
        ancestorsContinuing: childAncestors,
        visited,
        out,
      });
    });
  };

  return rates
    .filter((dr) => !isChild(dr))
    .map((parent) => {
      const descendants = [];
      collectDescendants({
        parentId: parent.id,
        depth: 1,
        ancestorsContinuing: [],
        visited: new Set([parent.id]),
        out: descendants,
      });
      return { parent, descendants };
    });
};
